import { useState, useMemo } from 'react'
import { colors, formatNumber } from '../styles/tokens'
import { Button, Input, Select } from './FormControls'
import { calculateFacade } from '../lib/quantityEngine'
import { useDraftPersistence } from '../lib/useDraftPersistence'

export default function FacadeModulePanel({ scopeId, boqItemId, superseding, onClose, onError, saving, onSave }) {
  const draftKey = `draft:facade:${scopeId || boqItemId || 'unknown'}`
  const [draft, setDraft, clearDraft] = useDraftPersistence(draftKey, { measurementMode: 'area', items: [] })
  const initial = superseding ? { measurementMode: superseding.engineering_inputs?.items?.[0]?.measurementMode || 'area', items: superseding.engineering_inputs?.items || [] } : draft

  const [measurementMode, setMeasurementModeRaw] = useState(initial.measurementMode)
  const [items, setItemsRaw] = useState(initial.items)
  const setMeasurementMode = (v) => { setMeasurementModeRaw(v); if (!superseding) setDraft((d) => ({ ...d, measurementMode: v })) }
  const setItems = (u) => setItemsRaw((p) => { const n = typeof u === 'function' ? u(p) : u; if (!superseding) setDraft((d) => ({ ...d, items: n })); return n })

  function addItem() { setItems((p) => [...p, { id: `I${Date.now()}`, label: '', length: '', height: '', geometryCount: '' }]) }
  function updateItem(id, patch) { setItems((p) => p.map((i) => (i.id === id ? { ...i, ...patch } : i))) }
  function removeItem(id) { setItems((p) => p.filter((i) => i.id !== id)) }

  const itemsForCalc = useMemo(() => items.map((i) => ({
    label: i.label, measurementMode,
    length: measurementMode === 'area' ? (i.length === '' ? NaN : Number(i.length)) : undefined,
    height: measurementMode === 'area' ? (i.height === '' ? NaN : Number(i.height)) : undefined,
    geometryCount: measurementMode === 'panel_count' ? (i.geometryCount === '' ? NaN : Number(i.geometryCount)) : undefined,
  })), [items, measurementMode])

  const result = useMemo(() => {
    if (itemsForCalc.length === 0) return { value: null, error: null }
    try { return { value: calculateFacade(itemsForCalc), error: null } } catch (e) { return { value: null, error: e.message } }
  }, [itemsForCalc])

  async function handleSave() {
    if (!result.value) return
    const engineeringInputs = { items: items.map((i) => ({ ...i, measurementMode })) }
    const succeeded = await onSave({ calculatedValueTon: result.value.finalQuantity, unit: result.value.finalUnit, geometrySnapshot: { items: [] }, engineeringInputs, breakdownSnapshot: result.value, takeoffItemIdForProvenance: null })
    if (succeeded !== false) clearDraft()
  }

  return (
    <div>
      <div style={{ padding: '8px 10px', background: '#FFF8E1', border: `1px solid #F5C542`, borderRadius: 6, fontSize: 11, color: '#7A5C00', marginBottom: 14 }}>
        ⚠ Area and Panel Count are independent measurement modes — they are never multiplied together. Choose one mode per Output.
      </div>
      <L label="Measurement Mode (applies to this whole Output)">
        <Select value={measurementMode} onChange={(e) => setMeasurementMode(e.target.value)} style={{ width: '100%' }}><option value="area">Area (m²)</option><option value="panel_count">Panel Count (No.)</option></Select>
      </L>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <label style={{ fontSize: 11, fontWeight: 700, color: colors.textSecondary, textTransform: 'uppercase' }}>Items</label>
        <Button size="sm" variant="secondary" onClick={addItem}>+ Add Item</Button>
      </div>
      {items.map((i) => (
        <div key={i.id} style={{ border: `1px solid ${colors.border}`, borderRadius: 6, padding: 8, marginBottom: 8 }}>
          <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
            <Input placeholder="Label" value={i.label} onChange={(e) => updateItem(i.id, { label: e.target.value })} style={{ flex: 1, fontSize: 11 }} />
            <button onClick={() => removeItem(i.id)} style={{ background: 'none', border: 'none', color: colors.danger, cursor: 'pointer' }}>✕</button>
          </div>
          {measurementMode === 'area' ? (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
              <L label="Length (m)"><Input type="number" value={i.length} onChange={(e) => updateItem(i.id, { length: e.target.value })} style={{ fontSize: 11, width: '100%' }} /></L>
              <L label="Height (m)"><Input type="number" value={i.height} onChange={(e) => updateItem(i.id, { height: e.target.value })} style={{ fontSize: 11, width: '100%' }} /></L>
            </div>
          ) : (
            <L label="Geometry Count (No.)"><Input type="number" value={i.geometryCount} onChange={(e) => updateItem(i.id, { geometryCount: e.target.value })} style={{ fontSize: 11, width: '100%' }} /></L>
          )}
        </div>
      ))}
      {result.value && (
        <div style={{ margin: '12px 0', padding: '10px 12px', background: '#F8FAFF', border: `1px solid ${colors.primary}`, borderRadius: 8, fontWeight: 700 }}>
          Total: {formatNumber(result.value.finalQuantity)} {result.value.finalUnit === 'm2' ? 'm²' : 'No.'}
        </div>
      )}
      {result.error && <div style={{ fontSize: 12, color: colors.textMuted, fontStyle: 'italic', marginBottom: 14 }}>{result.error}</div>}
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        {!superseding && <button onClick={() => { if (window.confirm('Discard draft?')) { clearDraft(); onClose() } }} style={{ background: 'none', border: 'none', color: colors.danger, cursor: 'pointer', fontSize: 12 }}>Discard Draft</button>}
        <Button onClick={handleSave} disabled={!result.value || saving} style={{ marginLeft: 'auto' }}>{saving ? 'Saving…' : superseding ? 'Save New Version' : 'Save Output'}</Button>
      </div>
    </div>
  )
}
function L({ label, children }) { return <div style={{marginBottom:10}}><label style={{ display: 'block', fontSize: 10, color: colors.textMuted, marginBottom: 2 }}>{label}</label>{children}</div> }
